/**
 * Grid digest for the Campaign Bulk Editor.
 *
 * The agent's whole view of the plan is a projection of the SAME rows the review
 * grid renders. That is deliberate: the row builders
 * (buildTtdReviewRows / buildDv360ReviewRows / buildGadsReviewRows /
 * buildGtReviewRows) are the only place that knows how a plan becomes rows, and a
 * second implementation on the backend would eventually disagree with the grid
 * about row ids or values. A bulk editor that addresses different rows than the
 * ones on screen is worse than no bulk editor, so we ship the grid's own rows.
 *
 * What gets sent is an allowlisted projection, not the whole row. Review rows
 * carry rendering scaffolding (`__refPath`, annotation blobs, resolved lookup
 * objects, per-cell issue metadata) that would bloat the payload and give the
 * agent fields it must not reason about. The allowlist is the bulk-relevant
 * surface plus the two derivation inputs a scaled-write disclosure needs
 * (`siblingCount`, `cpm`) and the one the Google Ads noun depends on (`fmt`).
 */

/** Keys forwarded when present. Anything not here never reaches the agent. */
const DIGEST_KEYS = [
  // identity / grouping
  'idx', 'fmt', 'tactic', 'package', 'category', 'market', 'assetKey',
  // flight — CLW spellings and GroundTruth's
  'start', 'end', 'start_date', 'end_date', 'flightType',
  // money + volume
  'budget', 'dailyBudget', 'dailyCap', 'campaignBudget', 'cpm', 'imps',
  'bidFloor', 'bidCeiling', 'bid_price', 'bid_type', 'pacing',
  'bidStrategy', 'bidGoal', 'targetLabel',
  // targeting
  'geo', 'geoIncludeList', 'geoExcludeList', 'zipcodes', 'zipcodeList',
  'audiences', 'audienceList', 'audienceEntries',
  'keywords', 'keywordList', 'negativeKeywords', 'negativeKeywordList',
  'interests', 'interestList', 'interestIds',
  'devices', 'deviceList', 'languages', 'languageList',
  'placements', 'placementList', 'age', 'gender',
  'environment', 'expansion', 'brandSafety', 'countryCode', 'matchType',
  'location_targeting', 'location_filter', 'neighborhood_type', 'radial',
  'targeting_product', 'device_types',
  // delivery / measurement
  'freqCap', 'viewability', 'kpi', 'kpiTarget', 'pixel', 'convWindow',
  'objective', 'optimizationGoal', 'billingEvent', 'attribution',
  // deals
  'pmp', 'pmpDealList', 'pmpInventory',
  // GroundTruth columns — every GT-editable cell the agent may address.
  // audienceList / location_group / tracking_visits_to are the rendered forms
  // of the gt-taxonomy picker cells (their writes go through list ops).
  'product', 'package_name', 'billability_type', 'optimization_strategy',
  'proximity_mode', 'drive_to_type', 'device_targeting',
  'targeting_identifier', 'demographic_targeting', 'special_notes',
  'creative_sizes', 'creative_type', 'location_group', 'tracking_visits_to',
  // UAT 0817 made these editable text — the agent needs to see them.
  'creative_versions', 'placement_320x50', 'placement_300x250',
  'placement_320x480', 'placement_id_728x90',
  // creative-ish fields a bulk edit can legitimately touch
  'headlines', 'headlineList', 'descriptions', 'descriptionList',
  'longHeadline', 'businessName', 'sitelinks', 'sitelinkList',
  'displayPath', 'clickURL', 'creative', 'creativeList',
  'videos', 'videoList', 'adCopy', 'adCopyList',
  // scaled-write inputs — without these a budget disclosure can't be computed
  'siblingCount',
];

// Yahoo metadata is opt-in so existing platforms keep byte-identical digests.
const YAHOO_DIGEST_KEYS = ['yahooLineId', 'flight', 'budgetType', 'currencyCode', 'goalType', 'goalAmount', 'geoExclude', 'yahooGeo', 'yahooGeoExcluded', 'yahooSegments', 'yahooCategories', 'yahooKeywords', 'yahooMailDomains', 'yahooReceipts', 'yahooDomains', 'yahooTargetEditable', 'yahooSegmentGroups', 'yahooReceiptAttribute', 'yahooReceiptType', 'yahooAudienceSummary', 'yahooTargetingState'];

/**
 * The parent label a selector's `parent:` filter matches against. Every row
 * builder publishes the owning entity under a slightly different key, so try
 * them in specificity order: `market` is the parent campaign / IO name on TTD,
 * DV360 and Google Ads; GroundTruth uses ioName / campaignName.
 */
const parentOf = (row) => row.market ?? row.ioName ?? row.campaignName ?? null;

/** One review row → one digest row. */
function digestRow(row) {
  const out = {
    rowId: row.rowId,
    name: row.name ?? null,
  };
  const parent = parentOf(row);
  if (parent !== null && parent !== undefined && parent !== '—') out.parent = parent;
  const keys = row.yahooLineId ? [...DIGEST_KEYS, ...YAHOO_DIGEST_KEYS] : DIGEST_KEYS;
  for (const k of keys) {
    const v = row[k];
    if (v === undefined || v === null) continue;
    // Objects on a digest row are rendering scaffolding, not values the agent
    // should compare against — drop them rather than shipping opaque blobs.
    // Arrays are real (list cells), so those stay.
    if (typeof v === 'object' && !Array.isArray(v)) continue;
    out[k] = v;
  }
  return out;
}

/**
 * Build the digest payload for a bulk-editor session.
 *
 * @param {Array}  reviewRows      the grid's rows, exactly as rendered
 * @param {Set|Array} editableFields the platform's editable grid keys
 * @returns {{ rows, editableFields }}
 */
export function buildBulkDigest(reviewRows, editableFields) {
  const rows = (Array.isArray(reviewRows) ? reviewRows : []).map(digestRow);
  return {
    rows,
    editableFields: [...(editableFields instanceof Set ? editableFields : (editableFields || []))],
  };
}

/**
 * Serialise a platform's list-field capabilities for the agent session.
 *
 * The bulk editor has to gate list ops (`geo add Seattle`) on the SAME config
 * CellDetailPanel gates its per-item controls on, because a field can be absent
 * from the scalar editable set while still being fully editable as a list — Copy
 * TTD `geo` is exactly that, and gating on the scalar set made the agent refuse
 * geo on a grid where a human edits it by hand.
 *
 * Only the three things that decide an op are shipped: which ops are granted,
 * whether adds need a resolved platform id, and the user-facing reason a config
 * gives when it withholds everything. Placeholders and hint text stay client-side.
 */
export function serializeListFields(listFields) {
  const out = {};
  for (const [field, cfg] of Object.entries(listFields || {})) {
    if (!cfg) continue;
    out[field] = {
      ops: {
        add: Boolean(cfg.ops?.add),
        edit: Boolean(cfg.ops?.edit),
        remove: Boolean(cfg.ops?.remove),
      },
      ...(cfg.lookup?.kind ? { lookup: { kind: cfg.lookup.kind } } : {}),
      ...(cfg.reason ? { reason: cfg.reason } : {}),
    };
  }
  return out;
}

/** rowId set for the staging validator's "is this row still in the grid" check. */
export const rowIdSet = (reviewRows) => new Set(
  (Array.isArray(reviewRows) ? reviewRows : []).map((r) => String(r.rowId)),
);
